<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-12">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="calendar" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Leave Requests</h3>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 bg-white">
          <div class="d-flex flex-wrap gap-3">
            <div class="flex-grow-1">
              <label class="form-label">Employee</label>
              <v-select
                  label="name"
                  v-model="filters.employee_id"
                  :reduce="employee => employee.id"
                  :options="employees"
                  class="style-chooser"
                  placeholder="Type to search"
                  @option:selected="filterLeaveRequests"
              ></v-select>
            </div>
            <div class="flex-grow-1">
              <label class="form-label">Status</label>
              <select class="form-select" v-model="filters.status" @change="filterLeaveRequests" data="">
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div class="flex-grow-1">
              <label class="form-label">From Date</label>
              <input type="date" class="form-control" v-model="filters.from_date" @change="filterLeaveRequests">
            </div>
            <div class="flex-grow-1">
              <label class="form-label">To Date</label>
              <input type="date" class="form-control" v-model="filters.to_date" @change="filterLeaveRequests">
            </div>
          </div>
          <div class="col-md-12 mt-3 text-end">
            <button class="btn btn-outline-secondary" @click="resetFilter">
              <vue-feather type="refresh-cw" size="16" class="me-1"></vue-feather>
              Reset Filters
            </button>
          </div>
        </Card>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="calendar" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Leave Requests ({{leaveRequests.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search leave request" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Leave Requests Table -->
          <div v-if="!loading && leaveRequests.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Employee</th>
                  <th scope="col" class="fw-semibold">Leave Type</th>
                  <th scope="col" class="fw-semibold">Start Date</th>
                  <th scope="col" class="fw-semibold">End Date</th>
                  <th scope="col" class="fw-semibold">Days</th>
                  <th scope="col" class="fw-semibold">Status</th>
                  <th scope="col" class="fw-semibold">Applied On</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(request, index) in filteredLeaveRequests" :key="request.id">
                  <td>
                    <a :href="`/employee/${request.employee.id}`" class="fw-semibold">
                      {{ request.employee.first_name }} {{ request.employee.middle_name }} {{ request.employee.last_name }}
                    </a>
                  </td>
                  <td>{{ request.leave_type.name }}</td>
                  <td>{{ formatDate(request.start_date) }}</td>
                  <td>{{ formatDate(request.end_date) }}</td>
                  <td>{{ request.days }}</td>
                  <td>
                    <span :class="getStatusBadgeClass(request.status)">
                      {{ capitalizeFirstLetter(request.status) }}
                    </span>
                  </td>
                  <td>{{ formatDate(request.created_at) }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary me-1" @click="viewLeaveRequest(request)">
                      <vue-feather type="eye" size="14"></vue-feather>
                    </button>
                    <button
                      v-if="request.status === 'pending' && isApprover"
                      class="btn btn-sm btn-outline-success me-1"
                      @click="openApproveModal(request)"
                    >
                      <vue-feather type="check" size="14"></vue-feather>
                    </button>
                    <button
                      v-if="request.status === 'pending' && isApprover"
                      class="btn btn-sm btn-outline-danger me-1"
                      @click="openRejectModal(request)"
                    >
                      <vue-feather type="x" size="14"></vue-feather>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && leaveRequests.length === 0" class="text-center py-5">
            <vue-feather type="calendar" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Leave Requests Found</h5>
            <p class="text-muted">No leave requests to display.</p>
          </div>
        </Card>
      </div>
    </div>

    <!-- View Leave Request Modal -->
    <ViewLeaveRequestModal
      :show="showViewModal"
      :request="selectedRequest"
      @close="closeViewModal"
      @reassign="handleReassign"
    />

    <!-- Reject Leave Request Modal -->
    <RejectLeaveRequestModal
      :show="showRejectModal"
      :loading="saving"
      @close="closeRejectModal"
      @reject="handleReject"
    />

    <!-- Approve Leave Request Modal -->
    <ApproveLeaveRequestModal
      :show="showApproveModal"
      :loading="saving"
      @close="closeApproveModal"
      @approve="handleApprove"
    />

  </div>
</template>

<script>
import leaveService from '@/api/leaveService';
import {decrypt} from '@/utils/crypto';
import notify from '@/utils/notify';
import formatDate from '@/utils/formatDate';
import ViewLeaveRequestModal from '@/components/leave/ViewLeaveRequestModal.vue';
import RejectLeaveRequestModal from '@/components/leave/RejectLeaveRequestModal.vue';
import ApproveLeaveRequestModal from '@/components/leave/ApproveLeaveRequestModal.vue';
import VueFeather from 'vue-feather';
import confirmDialog from "@/utils/confirmDialog";
import selectSearch from "@/utils/selectSearch";

export default {
  name: 'LeaveRequests',
  components: {
    VueFeather,
    ViewLeaveRequestModal,
    RejectLeaveRequestModal,
    ApproveLeaveRequestModal,
  },
  data() {
    return {
      loading: false,
      saving: false,
      leaveRequests: [],
      employees: [],
      approvers: [],
      dataTableSearch: '',
      selectedRequest: null,
      showViewModal: false,
      showApproveModal: false,
      showRejectModal: false,
      filters: {
        employee_id: '',
        status: '',
        from_date: '',
        to_date: ''
      },
      rejectForm: {
        rejection_reason: ''
      },
      approveForm: {
        comments: ''
      }
    };
  },

  methods: {
    formatDate,
    confirmDialog,
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'pending':
          return 'badge bg-warning';
        case 'approved':
          return 'badge bg-success';
        case 'rejected':
          return 'badge bg-danger';
        case 'cancelled':
          return 'badge bg-secondary';
        default:
          return 'badge bg-primary';
      }
    },
    async fetchLeaveRequests() {
      try {
        this.loading = true;
        this.leaveRequests = await leaveService.getLeaveRequests();
      } catch (error) {
        notify.alert.error('Failed to fetch leave requests');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async filterLeaveRequests() {
      try {
        this.loading = true;
        this.leaveRequests = await leaveService.filterLeaveRequests(this.filters);
      } catch (error) {
        notify.alert.error('Failed to filter leave requests');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchEmployees() {
      this.employees = await selectSearch.allEmployees()
    },
    async fetchApprovers() {
      try {
        this.approvers = await leaveService.getLeaveApprovers();
      } catch (error) {
        notify.alert.error('Failed to fetch leave approvers');
        console.error(error);
      }
    },
    viewLeaveRequest(request) {
      this.selectedRequest = request;
      this.showViewModal = true;
    },
    closeViewModal() {
      this.showViewModal = false;
      this.selectedRequest = null;
    },
    openRejectModal(request) {
      this.selectedRequest = request;
      this.rejectForm.rejection_reason = '';
      this.showRejectModal = true;
    },
    closeRejectModal() {
      this.showRejectModal = false;
      this.selectedRequest = null;
    },
    openApproveModal(request) {
      this.selectedRequest = request;
      this.approveForm.comments = '';
      this.showApproveModal = true;
    },
    closeApproveModal() {
      this.showApproveModal = false;
      this.selectedRequest = null;
    },
    async handleApprove(comments) {
      try {
        this.saving = true;
        await leaveService.approveLeaveRequest(this.selectedRequest.id, comments || '');
        this.closeApproveModal();
        await this.fetchLeaveRequests();
      } catch (error) {
        console.error('Error approving leave request:', error);
        notify.alert.error(error.response?.data?.message || 'Failed to approve leave request');
      } finally {
        this.saving = false;
      }
    },
    async handleReassign({ approvalId, userId }) {
      try {
        this.saving = true;
        let response = await leaveService.reassignLeaveApproval(approvalId, userId);
        if (response.status === 200) {
          await this.fetchLeaveRequests();
          let request = this.leaveRequests.find(request => request.id === response.data.leave_request_id);
          this.viewLeaveRequest(request);
        }
      } catch (error) {
        console.error('Error reassigning approval:', error);
        notify.alert.error(error.response?.data?.message || 'Failed to reassign approval');
      } finally {
        this.saving = false;
      }
    },
    async approveLeaveRequest() {
      // This method is kept for backward compatibility
      await this.handleApprove(this.approveForm.comments);
    },
    async handleReject(reason) {
      this.saving = true;
      leaveService.rejectLeaveRequest(this.selectedRequest.id, { rejection_reason: reason })
        .then(() => {
          this.closeRejectModal();
          this.fetchLeaveRequests();
        })
        .catch(error => {
          console.error('Error rejecting leave request:', error);
          notify.alert.error('Failed to reject leave request');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    rejectLeaveRequest() {
      // This method is kept for backward compatibility
      if (this.rejectForm.rejection_reason.trim()) {
        this.handleReject(this.rejectForm.rejection_reason.trim());
      }
    },
    async resetFilter() {
      window.location.reload()
    },
  },

  computed: {
    filteredLeaveRequests() {
      if (!this.dataTableSearch) return this.leaveRequests;

      const search = this.dataTableSearch.toLowerCase();
      return this.leaveRequests.filter(request =>
          (request.employee.first_name + ' ' + request.employee.last_name).toLowerCase().includes(search) ||
          request.leave_type.name.toLowerCase().includes(search) ||
          request.status.toLowerCase().includes(search)
      );
    },
    isApprover() {
      return this.approvers.some(approver => approver.user_id === this.profile.id);
    }
  },

  created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.fetchLeaveRequests();
    this.fetchEmployees();
    this.fetchApprovers();
  }
};
</script>
